import { useRef, useState } from "react"

import CloudUploadIcon from "@mui/icons-material/CloudUpload"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import TaskIcon from "@mui/icons-material/Task"
import {
  Stack,
  Box,
  Typography,
  IconButton,
  CircularProgress,
} from "@mui/material"
import { useTranslation } from "react-i18next"

import { ReadMoreButton } from "@/components"
import StyledHelperText from "@/components/StyledHelperText"

interface FileDropZoneTextContent {
  title: string
  acceptedFormatsText: string
  learnMoreTitle: string
  learnMoreDescription: string
}

interface FileDropZoneProps {
  textContent: FileDropZoneTextContent
  acceptedFileTypes: string[]
  isUploading: boolean
  showPreview: boolean
  isUploaded: boolean
  maxSize: number
  imagePreviewData?: string
  fileName?: string
  errorText?: string
  onFileSelect: (file: File) => void
  onFileRemove: () => void
}

const FileDropZone = ({
  textContent,
  acceptedFileTypes,
  isUploading,
  showPreview,
  isUploaded,
  maxSize,
  imagePreviewData,
  fileName,
  errorText,
  onFileSelect,
  onFileRemove,
}: FileDropZoneProps) => {
  const { t } = useTranslation()
  const { title, acceptedFormatsText, learnMoreTitle, learnMoreDescription } =
    textContent
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [dragActive, setDragActive] = useState(false)
  const [validationError, setValidationError] = useState<string>()

  const validateAndSelectFile = (file: File | null) => {
    if (!file) {
      setValidationError(undefined)
      return
    }

    if (!acceptedFileTypes.includes(file.type)) {
      setValidationError(
        t("passHolderDetails.form.errors.invalidFileFormat") +
          " " +
          acceptedFormatsText
      )
      return
    }

    const maxSizeBytes = maxSize * 1024 * 1024
    if (file.size > maxSizeBytes) {
      setValidationError(
        t("passHolderDetails.form.errors.fileTooLarge", { maxSize })
      )
      return
    }

    setValidationError(undefined)
    onFileSelect(file)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    validateAndSelectFile(e.target.files?.[0] ?? null)
  }

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    const isDragEnterOrOver = e.type === "dragenter" || e.type === "dragover"
    setDragActive(isDragEnterOrOver)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    validateAndSelectFile(e.dataTransfer.files?.[0] ?? null)
  }

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
    setValidationError(undefined)
    onFileRemove?.()
  }

  const displayError = validationError || errorText

  return (
    <Stack padding={2}>
      <Box>
        <Typography variant="body2" fontWeight="bold">
          {title}
        </Typography>
        <Typography variant="body3">
          {t("passHolderDetails.form.uploadImageDescription")}
        </Typography>
      </Box>
      <Box alignSelf="flex-start">
        <ReadMoreButton
          buttonLabel={t("passHolderDetails.form.readMoreLabel")}
          modalHeadline={learnMoreTitle}
        >
          <Typography variant="body2">{learnMoreDescription}</Typography>
        </ReadMoreButton>
      </Box>

      {isUploading || isUploaded ? (
        <Box border="1px solid" borderColor="grey.300">
          <Box display="flex" alignItems="center" justifyContent="center">
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              flexShrink={0}
              overflow="hidden"
              width={56}
              height={56}
            >
              {isUploading ? (
                <CircularProgress size={32} />
              ) : showPreview && isUploaded ? (
                <img
                  src={imagePreviewData}
                  alt="Uploaded file"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              ) : (
                <TaskIcon fontSize="large" />
              )}
            </Box>

            <Box flex={1} minWidth={0}>
              <Typography
                variant="body2"
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
              >
                {fileName?.replace(/\.[^.]+$/, "")}
              </Typography>
            </Box>

            <IconButton
              onClick={handleRemove}
              size="small"
              aria-label="Remove file"
            >
              <DeleteOutlineIcon />
            </IconButton>
          </Box>
        </Box>
      ) : (
        <Box
          component="label"
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          border="2px dashed"
          borderColor={dragActive ? "secondary.main" : "grey.400"}
          borderRadius={2}
          padding={4}
          textAlign="center"
          sx={{
            cursor: "pointer",
            backgroundColor: dragActive ? "action.hover" : "transparent",
            transition: "border-color 0.2s, background-color 0.2s",
            position: "relative",
            "&:hover": {
              borderColor: "secondary.main",
              backgroundColor: "action.hover",
            },
            "&:has(:focus-visible)": {
              outline: "2px solid",
              outlineColor: "primary.main",
              outlineOffset: "2px",
            },
          }}
        >
          <input
            type="file"
            accept={acceptedFileTypes.join(",")}
            onChange={handleInputChange}
            ref={fileInputRef}
            style={{
              clipPath: "inset(50%)",
              height: "1px",
              overflow: "hidden",
              position: "absolute",
              whiteSpace: "nowrap",
              width: "1px",
            }}
            aria-label="Select file"
          />

          <Stack spacing={1} alignItems="center">
            <CloudUploadIcon fontSize="large" />
            <Typography variant="body2" fontWeight="bold">
              {t("passHolderDetails.form.fileDropZoneText")}{" "}
              <Typography
                component="span"
                variant="body2"
                fontWeight="bold"
                color="primary"
                sx={{ textDecoration: "underline" }}
              >
                {t("passHolderDetails.form.fileDropZoneUnderlinedText")}
              </Typography>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {acceptedFormatsText}
            </Typography>
          </Stack>
        </Box>
      )}
      {displayError && (
        <StyledHelperText helperText={displayError} severity="error" />
      )}
    </Stack>
  )
}

export default FileDropZone
